import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Button, Stack, Card, Form } from "@curves/ui-base";

const initialActions = [
	{
		label: "搜尋",
		variant: "contained",
		type: "submit",
	},
];

/**
 * OverviewListSearchBarLayout
 *
 * 用於列表頁的搜尋區塊排版元件，包含一個包裹在卡片內的表單區域，
 * 可插入任意搜尋欄位內容與一組自定義的按鈕（如：搜尋、重設、下載）。
 *
 * 常見使用場景：總覽頁頂部的搜尋列排版。
 *
 * @component
 *
 * @param {Object} props
 * @param {string} [props.className] - 外層 Card 的額外 className。
 * @param {React.ReactNode} props.children - 放入 Form 中的欄位區塊。
 * @param {function} [props.onSubmit] - 表單送出時觸發的 callback。
 * @param {Array<Object>} [props.isShowActions] - 是否顯示按鈕列，預設為 false。
 * @param {Array<Object>} [props.actions] - 按鈕列的設定，預設會顯示「搜尋」按鈕。
 * @param {string|React.ReactNode} props.actions[].label - 按鈕文字。
 * @param {"submit"|"button"|"reset"} [props.actions[].type] - 按鈕類型。
 * @param {"contained"|"outlined"|"text"} [props.actions[].variant] - 按鈕樣式。
 * @param {function} [props.actions[].onClick] - 按鈕點擊事件。
 * @param {...any} [props.rest] - 傳遞給 Card 的其他 props。
 *
 * @returns {JSX.Element}
 */
const OverviewListSearchBarLayout = ({ className, children, onSubmit, actions = initialActions, isShowActions = false, ...rest }) => {
	return (
		<Card className={clsx(className, "mb-1")} {...rest}>
			<Card.CardContent className={"p-3"}>
				<Form onSubmit={onSubmit}>
					{React.isValidElement(children) ? React.cloneElement(children) : children}
					{isShowActions
						? actions && (
								<Stack className={"mt-2"} justifyContent={"end"} spacing={2}>
									{actions.map(({ label, variant = "outlined", onClick, ...rest }) => (
										<Button key={label} variant={variant} onClick={onClick} {...rest}>
											{label}
										</Button>
									))}
								</Stack>
						  )
						: null}
				</Form>
			</Card.CardContent>
		</Card>
	);
};

OverviewListSearchBarLayout.prototype = {
	className: PropTypes.string,
	children: PropTypes.node,
	onSubmit: PropTypes.func,
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.node.isRequired,
			type: PropTypes.oneOf(["submit", "button", "reset"]),
			variant: PropTypes.oneOf(["contained", "outlined", "text"]),
			onClick: PropTypes.func,
		})
	),
	isShowActions: PropTypes.bool,
};

export default OverviewListSearchBarLayout;
